import { mat3, vec2, vec3 } from "gl-matrix"

const DEGREE = 180 / Math.PI
const RADIAN = Math.PI / 180

export const degrees = (rad: number) => rad * DEGREE

export const radians = (deg: number) => deg * RADIAN

export const lerp = (t: number, a: number, b: number) => a * (1 - t) + b * t

export const inverseLerp = (x: number, a: number, b: number) => (x - a) / (b - a)

export const clamp = (val: number, min: number, max: number) => Math.max(min, Math.min(max, val))

export const lerpPoint = (t: number, a: vec2, b: vec2): vec2 => [
	lerp(t, a[0], b[0]),
	lerp(t, a[1], b[1]),
]

export const rotatePoint = (angle: number, point: vec2, origin: vec2): vec2 => {
	const cos = Math.cos(angle)
	const sin = Math.sin(angle)

	const x = point[0] - origin[0]
	const y = point[1] - origin[1]

	return [x * cos - y * sin + origin[0], x * sin + y * cos + origin[1]]
}

export function transformMat3(vec: vec3, mat: mat3): vec3 {
	const a = mat[0]
	const b = mat[1]
	const c = mat[2]
	const p = mat[3]
	const q = mat[4]
	const r = mat[5]
	const u = mat[6]
	const v = mat[7]
	const w = mat[8]

	const [x, y, z] = vec

	// prettier-ignore
	return [
    a * x + b * y + c * z,
    p * x + q * y + r * z,
    u * x + v * y + w * z
  ]
}

export function multiplyMat3ByVec3(mat: mat3, vec: vec3): vec3 {
	const x = mat[0] * vec[0] + mat[1] * vec[1] + mat[2] * vec[2]
	const y = mat[3] * vec[0] + mat[4] * vec[1] + mat[5] * vec[2]
	const z = mat[6] * vec[0] + mat[7] * vec[1] + mat[8] * vec[2]

	return [x, y, z]
}

export function multiplyMat3(matA: mat3, matB: mat3): mat3 {
	const a = matA[0]
	const b = matA[1]
	const c = matA[2]
	const p = matA[3]
	const q = matA[4]
	const r = matA[5]
	const u = matA[6]
	const v = matA[7]
	const w = matA[8]

	const alpha = matB[0]
	const beta = matB[1]
	const gamma = matB[2]
	const lambda = matB[3]
	const mu = matB[4]
	const nu = matB[5]
	const rho = matB[6]
	const sigma = matB[7]
	const tau = matB[8]

	// prettier-ignore
	return [
    a * alpha + b * lambda + c * rho, a * beta + b * mu + c * sigma, a * gamma + b * nu + c * tau,
    p * alpha + q * lambda + r * rho, p * beta + q * mu + r * sigma, p * gamma + q * nu + r * tau,
    u * alpha + v * lambda + w * rho, u * beta + v * mu + w * sigma, u * gamma + v * nu + w * tau
  ]
}

export function invertMat3(mat: mat3): mat3 {
	const a = mat[0]
	const b = mat[1]
	const c = mat[2]
	const d = mat[3]
	const e = mat[4]
	const f = mat[5]
	const g = mat[6]
	const h = mat[7]
	const i = mat[8]

	const detA = a * (e * i - f * h) - b * (i * d - f * g) + c * (d * h - e * g)

	const A = e * i - f * h
	const B = f * g - d * i
	const C = d * h - e * g
	const D = c * h - b * i
	const E = a * i - c * g
	const F = b * g - a * h
	const G = b * f - c * e
	const H = c * d - a * f
	const I = a * e - b * d

	// prettier-ignore
	return [
    A / detA, D / detA, G / detA,
    B / detA, E / detA, H / detA,
    C / detA, F / detA, I / detA
  ]
}

export function interpolateMat3(matA: mat3, matB: mat3, g: number) {
	const out = mat3.create()

	for (let i = 0; i < matA.length; i++) {
		out[i] = g * <number>matA[i] + (1 - g) * <number>matB[i]
	}

	return out
}

export function isIdentityMat3(mat: mat3) {
	if (mat.length !== 9) {
		return false
	}

	const identityMat3 = [1, 0, 0, 0, 1, 0, 0, 0, 1]

	for (let i = 0; i < 9; i++) {
		if (mat[i] !== identityMat3[i]) {
			return false
		}
	}

	return true
}

export function isEmptyMat3(mat: mat3) {
	if (mat.length !== 9) {
		return false
	}

	for (let i = 0; i < 9; i++) {
		if (mat[i] !== 0) {
			return false
		}
	}

	return true
}

export function vec3ToMat3Diagonal(vec: vec3): mat3 {
	const M = new Array(9).fill(0)

	for (let i = 0; i < 3; i++) {
		M[i * 3 + i] = vec[i] ?? 0.0
	}

	return M as mat3
}

export function projectPointOnSegment(p: vec2, a: vec2, b: vec2): vec2 {
	const l1 = (p[0] - a[0]) * (b[0] - a[0]) + (p[1] - a[1]) * (b[1] - a[1])
	const l2 = (a[0] - b[0]) ** 2 + (a[1] - b[1]) ** 2
	const t = l1 / l2

	return [a[0] + t * (b[0] - a[0]), a[1] + t * (b[1] - a[1])]
}

export function areaTriangle(a: vec2, b: vec2, c: vec2) {
	const [x1, y1] = a
	const [x2, y2] = b
	const [x3, y3] = c

	return (x1 * (y2 - y3) + x2 * (y3 - y1) + x3 * (y1 - y2)) * 0.5
}

export const getCentroid = (points: vec2[]): vec2 => {
	// close polygon
	points = points.concat([points[0] as vec2])

	const len = points.length
	let a = 0,
		x = 0,
		y = 0
	let p1, p2, f

	for (let i = 0, j = len - 1; i < len; j = i++) {
		p1 = points[i]
		p2 = points[j]
		f = p1[0] * p2[1] - p2[0] * p1[1]
		a += f
		x += (p1[0] + p2[0]) * f
		y += (p1[1] + p2[1]) * f
	}

	f = a * 3

	return [x / f, y / f]
}
